import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { DollarSign, TrendingUp, AlertCircle, Loader2, Send, FileText, FileCheck, Copy, Wallet } from "lucide-react";
import { toast } from "sonner";
import { DeleteButton } from "../components/DeleteButton";

const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const STATUS_COLOR = {
  em_aberto: "#3b82f6", pago: "#16a34a", atrasado: "#eab308", inadimplente: "#dc2626", critico: "#7f1d1d",
};

const Card1 = ({ icon: Icon, label, value, hint, color }) => (
  <Card className="p-5 border-l-4" style={{ borderLeftColor: color }}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
          <Icon size={12} /> {label}
        </div>
        <div className="text-2xl font-display font-bold mt-1.5" style={{ color: "#1B3A6B" }}>{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </div>
    </div>
  </Card>
);

export default function Financeiro() {
  const [tab, setTab] = useState("dashboard");
  const [kpis, setKpis] = useState(null);
  const [lanc, setLanc] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [busy, setBusy] = useState(null);
  const [pixView, setPixView] = useState(null);
  const token = localStorage.getItem("lab_token") || "";

  const load = async () => {
    const [d, l, c] = await Promise.all([
      api.get("/financeiro/dashboard"),
      api.get("/lancamentos"),
      api.get("/clientes"),
    ]);
    setKpis(d.data);
    setLanc(l.data);
    setClientes(c.data);
  };
  useEffect(() => { load(); }, []);

  const cliMap = Object.fromEntries(clientes.map((c) => [c.id, c.nome]));

  const gerarCobranca = async (l) => {
    setBusy(`gerar-${l.id}`);
    try {
      const { data } = await api.post(`/cobrancas/lancamentos/${l.id}/gerar`);
      toast.success(`Cobrança gerada · ${data.asaas_payment_id}`);
      load();
    } catch (e) { toast.error("Falha"); }
    finally { setBusy(null); }
  };

  const registrarManual = async (l) => {
    setBusy(`pag-${l.id}`);
    try {
      const { data } = await api.post(`/cobrancas/lancamentos/${l.id}/registrar-pagamento?forma=manual`);
      toast.success(`Pago · NFS-e ${data.nfse_numero}`);
      load();
    } catch (e) { toast.error("Falha"); }
    finally { setBusy(null); }
  };

  const emitirNFSe = async (l) => {
    setBusy(`nfse-${l.id}`);
    try {
      const { data } = await api.post(`/nfse/lancamentos/${l.id}/emitir`);
      toast.success(`NFS-e ${data.numero}`);
      load();
    } catch (e) { toast.error("Falha"); }
    finally { setBusy(null); }
  };

  const copyPix = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Pix copia-cola copiado");
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>
          Financeiro unificado
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Cobrança automática (Asaas) · NFS-e · Conciliação · Comercial + Locativo
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="lancamentos" data-testid="tab-lancamentos">Lançamentos</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-5 mt-4">
          {kpis && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card1 icon={TrendingUp} label="MRR" value={fmt(kpis.mrr)} hint="Contratos ativos" color="#F97316" />
                <Card1 icon={DollarSign} label="Recebido no mês" value={fmt(kpis.recebido_mes)} hint={`${kpis.qtd_pagos_mes} pagamentos`} color="#16a34a" />
                <Card1 icon={Wallet} label="A receber" value={fmt(kpis.a_receber_mes)} hint={`${kpis.qtd_em_aberto} em aberto`} color="#3b82f6" />
                <Card1 icon={AlertCircle} label="Inadimplência" value={fmt(kpis.inadimplencia)} hint={`${kpis.qtd_inadimplentes} em atraso`} color="#dc2626" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="p-5">
                  <h3 className="font-display font-bold mb-3" style={{ color: "#1B3A6B" }}>Receita últimos 6 meses</h3>
                  <div className="space-y-2">
                    {kpis.serie_6m.map((s) => {
                      const max = Math.max(...kpis.serie_6m.map((x) => x.recebido), 1);
                      const pct = (s.recebido / max) * 100;
                      return (
                        <div key={s.mes} className="flex items-center gap-3">
                          <div className="text-xs w-14 text-muted-foreground">{s.mes}</div>
                          <div className="flex-1 bg-slate-100 rounded-md h-7 relative overflow-hidden">
                            <div
                              className="h-full rounded-md flex items-center justify-end pr-2 text-xs text-white font-semibold"
                              style={{ width: `${Math.max(pct, 3)}%`, background: "#F97316" }}
                            >
                              {fmt(s.recebido)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>

                <Card className="p-5">
                  <h3 className="font-display font-bold mb-3" style={{ color: "#1B3A6B" }}>Top 10 clientes (faturado)</h3>
                  {kpis.top_clientes.length === 0 && <p className="text-sm text-muted-foreground">Ainda sem pagamentos confirmados.</p>}
                  <div className="space-y-2">
                    {kpis.top_clientes.map((c, i) => {
                      const max = Math.max(...kpis.top_clientes.map((x) => x.valor), 1);
                      const pct = (c.valor / max) * 100;
                      return (
                        <div key={c.cliente + i} className="flex items-center gap-3">
                          <div className="text-xs w-32 truncate" style={{ color: "#1B3A6B" }}>{c.cliente}</div>
                          <div className="flex-1 bg-slate-100 rounded-md h-6 relative overflow-hidden">
                            <div className="h-full rounded-md flex items-center justify-end pr-2 text-xs text-white font-semibold" style={{ width: `${Math.max(pct, 3)}%`, background: "#1B3A6B" }}>
                              {fmt(c.valor)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="lancamentos" className="mt-4">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Venc</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Cobrança</TableHead>
                  <TableHead>NFS-e</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lanc.map((l) => (
                  <TableRow key={l.id} data-testid={`fin-row-${l.id}`}>
                    <TableCell className="font-medium text-sm">{cliMap[l.cliente_id] || "—"}</TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate">{l.descricao}</TableCell>
                    <TableCell className="text-xs">{(l.vencimento || "").slice(0, 10)}</TableCell>
                    <TableCell className="text-right font-semibold">{fmt(l.valor)}</TableCell>
                    <TableCell>
                      <Badge style={{ background: STATUS_COLOR[l.status] || "#6b7280", color: "white" }}>
                        {l.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {l.asaas_payment_id ? (
                        <div className="flex items-center gap-1">
                          {l.boleto_url && (
                            <a href={l.boleto_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs" data-testid={`boleto-${l.id}`}>
                              Boleto
                            </a>
                          )}
                          {l.pix_copia_cola && (
                            <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => setPixView(l)} data-testid={`pix-${l.id}`}>
                              Pix
                            </Button>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {l.nfse_numero ? (
                        <div className="flex items-center gap-1">
                          <FileCheck size={12} className="text-green-600" />
                          {l.nfse_url ? (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${l.nfse_url}?auth=${token}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              {l.nfse_numero}
                            </a>
                          ) : (
                            <span>{l.nfse_numero}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      {!l.asaas_payment_id && (
                        <Button size="sm" variant="outline" onClick={() => gerarCobranca(l)} disabled={busy === `gerar-${l.id}`} data-testid={`gerar-${l.id}`}>
                          {busy === `gerar-${l.id}` ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
                          Gerar
                        </Button>
                      )}
                      {l.status !== "pago" && (
                        <Button size="sm" className="text-white" style={{ background: "#16a34a" }} onClick={() => registrarManual(l)} disabled={busy === `pag-${l.id}`} data-testid={`pag-${l.id}`}>
                          {busy === `pag-${l.id}` ? <Loader2 size={10} className="animate-spin" /> : <DollarSign size={10} />}
                          Pago
                        </Button>
                      )}
                      {l.status === "pago" && !l.nfse_numero && (
                        <Button size="sm" variant="outline" onClick={() => emitirNFSe(l)} disabled={busy === `nfse-${l.id}`} data-testid={`nfse-${l.id}`}>
                          NFS-e
                        </Button>
                      )}
                      {l.recibo_url && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${l.recibo_url}?auth=${token}`} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="ghost"><FileText size={10} /></Button>
                        </a>
                      )}
                      <DeleteButton
                        url={`/lancamentos/${l.id}`}
                        label={`Lançamento ${(cliMap[l.cliente_id] || l.id.slice(0, 8))} · ${fmt(l.valor)}`}
                        warningText={l.asaas_payment_id ? "Este lançamento já possui cobrança gerada no Asaas." : null}
                        onDeleted={load}
                        testId={`del-lanc-${l.id}`}
                        iconOnly
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!pixView} onOpenChange={(o) => !o && setPixView(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Pagar com Pix</DialogTitle></DialogHeader>
          {pixView && (
            <div className="space-y-3">
              {pixView.pix_qrcode_url && (
                <div className="flex justify-center">
                  <img src={pixView.pix_qrcode_url} alt="QR Code" className="h-48 w-48 border rounded-md" />
                </div>
              )}
              <div className="text-xs text-center text-muted-foreground">Ou copie o código abaixo:</div>
              <div className="flex gap-2">
                <code className="flex-1 text-xs p-2 bg-slate-100 rounded break-all max-h-32 overflow-y-auto">
                  {pixView.pix_copia_cola}
                </code>
                <Button size="sm" onClick={() => copyPix(pixView.pix_copia_cola)} className="text-white" style={{ background: "#F97316" }}>
                  <Copy size={12} className="mr-1" /> Copiar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
